/**
 * Sound utility for playing notification sounds
 * Handles browser autoplay restrictions gracefully
 * Respects user preferences for sound notifications
 */

import { useUserPreferencesStore } from '@/store/user-preferences.store'

import {
  isBrowserNotificationPreferences,
  PreferenceContextEnum,
} from '@scrambl/shared'

let soundVolume = 0.5

/**
 * Create audio only when a sound genuinely needs to play. Constructing and
 * preloading media during app startup can make Bluetooth headphones switch
 * their active connection to the desktop even though nothing is audible.
 */
function createSound(src: string): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null

  const sound = new Audio(src)
  sound.volume = soundVolume
  return sound
}

/**
 * Do not let a background socket event create Chrome's first media session.
 * Besides being blocked by autoplay policy, that attempted playback can make
 * Bluetooth headphones hand off to the computer while the page is loading.
 */
function hasUserActivation(): boolean {
  if (typeof navigator === 'undefined') return false
  return navigator.userActivation?.hasBeenActive ?? true
}

/** Release Chrome's media player as soon as the short notification ends. */
function releaseSound(sound: HTMLAudioElement): void {
  sound.pause()
  sound.removeAttribute('src')
  sound.load()
}

function playSound(src: string): void {
  if (!hasUserActivation()) return

  const sound = createSound(src)
  if (!sound) return

  let released = false
  const release = () => {
    if (released) return
    released = true
    releaseSound(sound)
  }

  sound.addEventListener('ended', release, { once: true })
  sound.addEventListener('error', release, { once: true })
  void sound.play().catch(release)
}

/**
 * Check if sounds are enabled in user preferences
 * Defaults to true if no preference is set
 */
function isSoundEnabled(): boolean {
  const { getPreference } = useUserPreferencesStore.getState()
  const preference = getPreference(PreferenceContextEnum.NOTIFICATIONS)

  if (
    preference?.preferences &&
    isBrowserNotificationPreferences(preference.preferences)
  ) {
    // Default to true if playSound is not explicitly set
    return preference.preferences.playSound ?? true
  }

  // Default to enabled if no preference exists
  return true
}

/**
 * Play the new message sound (for thread messages)
 * Respects user preference for sound notifications
 * Catches autoplay restrictions silently
 */
export function playNewMessageSound(): void {
  if (!isSoundEnabled()) return
  playSound('/sounds/new-message.mp3')
}

/**
 * Play the new notification sound (for notification widget)
 * Respects user preference for sound notifications
 * Catches autoplay restrictions silently
 */
export function playNewNotificationSound(): void {
  if (!isSoundEnabled()) return
  playSound('/sounds/new-notification.mp3')
}

/**
 * Set volume for all sounds (0.0 to 1.0)
 */
export function setSoundVolume(volume: number): void {
  soundVolume = Math.max(0, Math.min(1, volume))
}
